// Sidebar component from shadcn

import * as React from 'react';
import { Slot } from 'radix-ui';
import { PanelLeftIcon } from 'lucide-react';
import { cn } from '@/utilities/cn';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

const MOBILE_BREAKPOINT = 768;
const SIDEBAR_WIDTH = '18rem';
const SIDEBAR_WIDTH_ICON = '4.5rem';

type SidebarState = 'expanded' | 'collapsed';

type SidebarContextValue = {
  state: SidebarState;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openMobile: boolean;
  setOpenMobile: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

function useSidebarMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  React.useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT - 1}px)`
    );

    const update = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    update();
    mediaQuery.addEventListener('change', update);

    return () => {
      mediaQuery.removeEventListener('change', update);
    };
  }, []);

  return isMobile;
}

function useSidebar() {
  const context = React.useContext(SidebarContext);

  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  onMobileOpenChange,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onMobileOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useSidebarMobile();
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const [openMobile, setOpenMobile] = React.useState(false);
  const open = openProp ?? internalOpen;
  const state: SidebarState = open ? 'expanded' : 'collapsed';

  const setOpen = React.useCallback(
    (nextOpen: React.SetStateAction<boolean>) => {
      const resolvedOpen =
        typeof nextOpen === 'function' ? nextOpen(open) : nextOpen;

      onOpenChange?.(resolvedOpen);
      if (openProp === undefined) {
        setInternalOpen(resolvedOpen);
      }
    },
    [onOpenChange, open, openProp]
  );

  const setOpenMobileTracked = React.useCallback(
    (nextOpen: React.SetStateAction<boolean>) => {
      setOpenMobile((currentOpen) => {
        const resolvedOpen =
          typeof nextOpen === 'function' ? nextOpen(currentOpen) : nextOpen;
        onMobileOpenChange?.(resolvedOpen);
        return resolvedOpen;
      });
    },
    [onMobileOpenChange]
  );

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobileTracked((current) => !current);
      return;
    }

    setOpen((current) => !current);
  }, [isMobile, setOpen, setOpenMobileTracked]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const pressedKey = event.key?.toLowerCase();
      if (pressedKey !== 'b' || (!event.metaKey && !event.ctrlKey)) {
        return;
      }

      event.preventDefault();
      toggleSidebar();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleSidebar]);

  return (
    <SidebarContext.Provider
      value={{
        state,
        open,
        setOpen,
        openMobile,
        setOpenMobile: setOpenMobileTracked,
        isMobile,
        toggleSidebar,
      }}
    >
      <div
        data-slot="sidebar-wrapper"
        data-state={state}
        className={cn('flex min-h-svh w-full bg-sidebar/30', className)}
        style={style}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  className,
  children,
  side = 'left',
  collapsible = 'icon',
  mobileTitle = 'Library navigation',
  mobileDescription = 'Browse library navigation and host controls.',
  showMobileCloseButton = false,
  style,
  ...props
}: React.ComponentProps<'aside'> & {
  side?: 'left' | 'right';
  collapsible?: 'icon' | 'offcanvas' | 'none';
  mobileTitle?: React.ReactNode;
  mobileDescription?: React.ReactNode;
  showMobileCloseButton?: boolean;
}) {
  const { isMobile, openMobile, setOpenMobile, state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side={side}
          showCloseButton={showMobileCloseButton}
          style={{ width: SIDEBAR_WIDTH, ...style }}
          className={cn(
            'border-sidebar-border bg-sidebar p-0 text-sidebar-foreground',
            className
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{mobileTitle}</SheetTitle>
            <SheetDescription>{mobileDescription}</SheetDescription>
          </SheetHeader>
          <div className="flex h-full min-h-0 flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  const desktopWidth =
    collapsible === 'none'
      ? SIDEBAR_WIDTH
      : isCollapsed
        ? collapsible === 'offcanvas'
          ? '0rem'
          : SIDEBAR_WIDTH_ICON
        : SIDEBAR_WIDTH;

  return (
    <aside
      data-slot="sidebar"
      data-side={side}
      data-state={state}
      data-collapsible={isCollapsed ? collapsible : undefined}
      className={cn(
        'group/sidebar hidden h-svh shrink-0 overflow-hidden bg-sidebar text-sidebar-foreground transition-[width,border-color] duration-200 ease-out md:flex md:flex-col',
        side === 'left'
          ? 'border-r border-sidebar-border'
          : 'border-l border-sidebar-border',
        collapsible === 'offcanvas' && isCollapsed && 'border-sidebar-border/0',
        className
      )}
      style={{ width: desktopWidth, ...style }}
      {...props}
    >
      {children}
    </aside>
  );
}

const SidebarTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon-sm"
      className={cn(className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">Toggle sidebar</span>
    </Button>
  );
});

SidebarTrigger.displayName = 'SidebarTrigger';

function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        'flex min-w-0 flex-1 flex-col bg-background transition-[margin] duration-200 ease-out',
        className
      )}
      {...props}
    />
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      className={cn('border-b border-sidebar-border p-3', className)}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto', className)}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      className={cn('border-t border-sidebar-border p-3', className)}
      {...props}
    />
  );
}

function SidebarGroup({
  className,
  ...props
}: React.ComponentProps<'section'>) {
  return (
    <section
      data-slot="sidebar-group"
      className={cn('flex flex-col gap-2 px-3 py-3', className)}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-label"
      className={cn(
        'px-3 text-xs font-semibold tracking-[0.12em] text-sidebar-foreground/60 uppercase',
        className
      )}
      {...props}
    />
  );
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      className={cn('flex flex-col gap-1', className)}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      className={cn('flex flex-col gap-1', className)}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li data-slot="sidebar-menu-item" className={cn(className)} {...props} />
  );
}

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
  }
>(({ asChild = false, isActive = false, className, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="sidebar-menu-button"
      data-active={isActive}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-sidebar-foreground transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-none data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        className
      )}
      {...props}
    />
  );
});

SidebarMenuButton.displayName = 'SidebarMenuButton';

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      className={cn(
        'ml-5 flex flex-col gap-1 border-l border-sidebar-border pl-3',
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      className={cn(className)}
      {...props}
    />
  );
}

const SidebarMenuSubButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
  }
>(({ asChild = false, isActive = false, className, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="sidebar-menu-sub-button"
      data-active={isActive}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm text-sidebar-foreground/80 transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-none data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        className
      )}
      {...props}
    />
  );
});

SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
};
